/**
 * Session manager for tracking delivery sessions (web).
 * iOS uses GPS logging boundaries instead — see `DeliverySessionManager` in the mobile app.
 * Here we approximate “pass” boundaries with idle time because the map has no logging toggle.
 */

const SESSION_STORAGE_KEY = "centerReach_deliverySessionId";
const SESSION_TIMESTAMP_KEY = "centerReach_deliverySessionTimestamp";
/** No `getCurrentSessionId()` for this long ⇒ new session (e.g. lunch ~1h ⇒ two sessions in a day). */
const SESSION_MAX_IDLE_MS = 60 * 60 * 1000;

/**
 * Gets or creates a new delivery session ID
 * A new session is created if:
 * - No session exists
 * - Nothing has touched this session for SESSION_MAX_IDLE_MS (timestamp slides on each call)
 */
export const getCurrentSessionId = (): string => {
    const storedId = localStorage.getItem(SESSION_STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    const now = Date.now();

    if (storedId && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const age = now - timestamp;

        // If session is still valid, extend the window and return the same id
        if (age < SESSION_MAX_IDLE_MS) {
            localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
            return storedId;
        }

        // Session expired, clear it
        localStorage.removeItem(SESSION_STORAGE_KEY);
        localStorage.removeItem(SESSION_TIMESTAMP_KEY);
    }

    // Create new session
    const newSessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());

    return newSessionId;
};

/**
 * Resets the current session (creates a new one)
 */
export const resetSession = (): void => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
};

/**
 * Gets the current session ID without creating a new one
 * Returns null if no session exists or session is expired
 */
export const getCurrentSessionIdIfValid = (): string | null => {
    const storedId = localStorage.getItem(SESSION_STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    
    if (storedId && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const age = Date.now() - timestamp;
        
        if (age < SESSION_MAX_IDLE_MS) {
            return storedId;
        }
    }
    
    return null;
};
